import type { AnyListenerPredicate } from '@reduxjs/toolkit';

/**
 * **Predicator** is a class designed to simplify creation of common predicate function for listener middleware.
 * */
export class Predicator<RootState> {
  /**
   * **all** is a method that creates a predicate function that checks if all the samples returned by the probe function are set.
   * Depending on the mode, it also checks if the samples have changed.
   * @param probe - A function that returns an array of samples to be checked.
   * @param options - An object that contains the mode of comparison.
   * @param options.mode - A string that defines the mode of comparison. It can be either 'change' or 'once'. Default is 'once'. If set to 'change' it predicate will return true if all samples are set and at least one of them has changed.
   * @returns A predicate function that checks if all the samples are set and if they have changed.
   * */
  all(
    probe: (state: RootState) => unknown[],
    options: {
      mode: 'change' | 'once';
    } = {
      mode: 'once',
    }
  ): AnyListenerPredicate<RootState> {
    return (_action, currentState, previousState) => {
      const currentSamples = probe(currentState);
      const allGotSet = currentSamples.every((sample) => !!sample);
      // First we check if all samples even are set
      if (!allGotSet) return false;
      const previousSamples = probe(previousState);
      // Then we check if the samples have changed, based on comparison mode
      if (options.mode === 'once') {
        // If mode is "once", we check if any of the were not set in previous state.
        // Because at this point we know that all are set in current state.
        // And if all are not set in previous state, then we know that they have settled as a result of current state change.
        return previousSamples.some((sample) => !sample);
      } else {
        // If mode is difference, we check if any of the samples have changed.
        return currentSamples.some((sample, index) => sample !== previousSamples[index]);
      }
    };
  }
}
